import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DateTimePicker, Field, Modal, toISODate, useConfirm } from "@signet/ui";
import { BAN_REASON, type BanReason } from "../../shared/lifecycle.ts";
import { APPEAL_STATE } from "../../shared/appeal.ts";
import { useStep } from "./account.tsx";
import { fetchStock, shortDate, stockKeys, type Ban, type Stock } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Every account's status, as three buttons (Jay, 2026-10-12):

     Not posting           nobody is posting to it for now
     Warming up & posting  the ticks say which, so the button names the day
     Banned                with why (connected, or 3 reasons) and the date

   They replace Mark restricted and Mark suspended. Leaving a ban for either
   of the other two asks first, since it throws away the ban's record.
   ────────────────────────────────────────────────────────────────────────── */

type Choice = "paused" | "active" | "banned";

const banned = (a: Stock) => a.status === "banned" || a.status === "suspended";
const current = (a: Stock): Choice => (banned(a) ? "banned" : a.pausedOn ? "paused" : "active");

/** The middle button's name: the day the ticks are on, or Active once the link is in. */
export function liveLabel(a: Stock): string {
  if (banned(a) || a.pausedOn) return "Warming up & posting";
  switch (a.step.stage) {
    case "warmup":
    case "posting":
      return a.step.title.replace(/ of \d+$/, "");
    case "add_link":
      return "Posting, link next";
    case "unassigned":
      return "Warming up & posting";
    default:
      return "Active";
  }
}

/** "Banned 12 Oct, 3 reason ban, asked for ID", "Banned 12 Oct, connected ban from @x". */
export function banLine(a: Stock): string {
  if (a.status === "suspended") return `Suspended${a.statusOn ? ` ${shortDate(a.statusOn)}` : ""}`;
  const reason = a.banReason ? BAN_REASON[a.banReason].toLowerCase() : null;
  return [
    `Banned${a.statusOn ? ` ${shortDate(a.statusOn)}` : ""}`,
    reason && a.banReason === "connected" && a.causedBy ? `${reason} from @${a.causedBy.username}` : reason,
    a.banReason === "three_reasons" && a.appealState ? APPEAL_STATE[a.appealState].toLowerCase().replace(/\bid\b/, "ID") : null,
  ].filter(Boolean).join(", ");
}

/** A 3 reason ban, banned now or once: one a connected ban can come from. */
export const isThreeReason = (a: Stock) => Boolean(a.appealState) || (a.status === "banned" && a.banReason === "three_reasons");

export function StatusButtons({ a }: { a: Stock }) {
  const step = useStep();
  const { confirm, element } = useConfirm();
  const [banning, setBanning] = useState(false);
  const now = current(a);

  const leaveBan = async () =>
    !banned(a) || (await confirm({ title: `Undo the ban on @${a.username}?`, body: "Its ban reason, date and appeal are cleared.", confirmLabel: "Undo the ban" }));

  const pick = async (choice: Choice) => {
    if (choice === "banned") return setBanning(true);
    if (choice === now) return;
    if (!(await leaveBan())) return;
    step.mutate({ id: a.id, step: choice });
  };

  const options: { value: Choice; label: string }[] = [
    { value: "paused", label: "Not posting" },
    { value: "active", label: liveLabel(a) },
    { value: "banned", label: "Banned" },
  ];

  return (
    <>
      {element}
      <div className="igs-status-buttons" role="radiogroup" aria-label={`Status of @${a.username}`}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={now === o.value}
            className={`igs-status-button ${o.value}${now === o.value ? " on" : ""}`}
            disabled={step.isPending}
            onClick={() => void pick(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
      <BanModal account={banning ? a : null} onClose={() => setBanning(false)} />
    </>
  );
}

/**
 * Banned: why, and on which day (today unless said otherwise). A connected
 * ban names the 3 reason ban on the same phone that took it down, if the
 * poster knows; a 3 reason ban starts its appeal as Appealed, since the team
 * appeals it straight away (Jess, 2026-10-15). onBan is the Ban check's sheet
 * accounts, which have no phone and no appeal here.
 */
export function BanModal({
  account, onClose, onBan,
}: {
  account: { id: string; username: string; banReason?: BanReason | null; statusOn?: string | null; status?: string; phone?: string | null; poster?: string | null; causedBy?: { id: string } | null } | null;
  onClose: () => void;
  onBan?: (ban: { reason: BanReason; on: string }) => Promise<unknown>;
}) {
  const step = useStep();
  const [reason, setReason] = useState<BanReason | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [cause, setCause] = useState<string | null | undefined>(undefined);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const onPhones = !onBan;
  const list = useQuery({ queryKey: stockKeys.list("all"), queryFn: fetchStock, enabled: Boolean(account) && onPhones });
  if (!account) return null;
  const today = toISODate(new Date());
  const chosen = reason ?? account.banReason ?? null;
  const on = day ?? (account.status === "banned" && account.statusOn ? account.statusOn : today);
  const future = on > today;
  // The 3 reason bans on this phone: the first ban on a phone is always one (the boss, 2026-10-15).
  const causes = onPhones && account.phone
    ? (list.data?.accounts ?? []).filter((a) => a.id !== account.id && a.phone === account.phone && a.poster === account.poster && isThreeReason(a))
    : [];
  const pickedCause = cause === undefined ? account.causedBy?.id ?? (causes.length === 1 ? causes[0]!.id : null) : cause;
  const alreadyThree = account.status === "banned" && account.banReason === "three_reasons";

  const close = () => {
    setReason(null);
    setDay(null);
    setCause(undefined);
    setNote("");
    onClose();
  };
  const save = async () => {
    if (!chosen || future) return;
    setBusy(true);
    try {
      if (onBan) await onBan({ reason: chosen, on });
      else {
        const ban: Ban = chosen === "connected" ? { reason: chosen, on, causedBy: pickedCause } : { reason: chosen, on, note: note.trim() || null };
        await step.mutateAsync({ id: account.id, step: "banned", ban });
      }
      close();
    } catch {
      // The mutation's own error toast has said what went wrong; the dialog stays open to try again.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={close}
      title={`@${account.username} is banned`}
      description="Say what kind of ban it is and the day it happened."
      footer={
        <>
          <button type="button" className="secondary-button" onClick={close}>Cancel</button>
          <button type="button" className="button danger" disabled={!chosen || future || busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Mark banned"}
          </button>
        </>
      }
    >
      <div className="igs-ban">
        <div className="igs-ban-reasons" role="radiogroup" aria-label="Kind of ban">
          {(["three_reasons", "connected"] as BanReason[]).map((r) => (
            <button key={r} type="button" role="radio" aria-checked={chosen === r} className={`igs-ban-reason${chosen === r ? " on" : ""}`} onClick={() => setReason(r)}>
              {BAN_REASON[r]}
            </button>
          ))}
        </div>
        {onPhones && chosen === "connected" ? (
          <Field label="Banned because of">
            {causes.length ? (
              <div className="igs-ban-causes" role="radiogroup" aria-label="The 3 reason ban it came from">
                {causes.map((c) => (
                  <button key={c.id} type="button" role="radio" aria-checked={pickedCause === c.id} className={`igs-ban-cause${pickedCause === c.id ? " on" : ""}`} onClick={() => setCause(c.id)}>
                    @{c.username}
                    <span>{c.appealState ? APPEAL_STATE[c.appealState] : "3 reason ban"}</span>
                  </button>
                ))}
                <button type="button" role="radio" aria-checked={pickedCause === null} className={`igs-ban-cause${pickedCause === null ? " on" : ""}`} onClick={() => setCause(null)}>
                  Not sure
                  <span>It may have come from another phone</span>
                </button>
              </div>
            ) : (
              <p className="faint igs-ban-hint">No 3 reason ban on phone {account.phone} yet. Mark that one first to link this one to it, or save it as it is.</p>
            )}
            {/* Where it goes depends on the cause (the boss, 2026-10-21), so say it before saving. */}
            <p className="faint igs-ban-hint">
              {pickedCause
                ? "It stays on the phone while that appeal is open, and goes to the Graveyard if it doesn't come back."
                : "With no 3 reason ban linked it can't come back, so it goes straight to the Graveyard."}
            </p>
          </Field>
        ) : null}
        {onPhones && chosen === "three_reasons" && !alreadyThree ? (
          <>
            <p className="faint igs-ban-hint">Appeal it now. It goes on the 3 reason bans list as Appealed, where its updates are kept. Then log it out of the phone and press Removed from phone.</p>
            <Field label="Note (optional)">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} placeholder="Appealed from the phone, waiting to hear back" />
            </Field>
          </>
        ) : null}
        <Field label="Banned on" error={future ? "The ban date can't be in the future." : undefined}>
          <DateTimePicker
            label="Banned on"
            value={new Date(`${on}T00:00:00`).toISOString()}
            onChange={(v) => setDay(v ? toISODate(new Date(v)) : null)}
            withTime={false}
            clearable={false}
          />
        </Field>
      </div>
    </Modal>
  );
}
